import { tr } from "@/i18n/copy";
// Adapt large raster uploads before they reach the server's 5 MB limit.
// PDFs and other documents are never re-encoded here.
export const IMAGE_UPLOAD_TARGET = 4 * 1024 * 1024;

export async function compressLargeImage(file, maxBytes = IMAGE_UPLOAD_TARGET) {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type) || file.size <= maxBytes) return file;
  if (file.size > 80 * 1024 * 1024) throw new Error(tr("Immagine troppo grande da elaborare: scegli un file inferiore a 80 MB."));
  const url = URL.createObjectURL(file);
  const source = new Image();
  const canvas = document.createElement("canvas");
  try {
    await new Promise((resolve, reject) => {
      source.onload = resolve;
      source.onerror = () => reject(new Error(tr("Immagine non leggibile. Usa JPG, PNG o WebP.")));
      source.src = url;
    });
    if (source.naturalWidth * source.naturalHeight > 80000000) throw new Error(tr("Immagine con dimensioni eccessive. Scegli una risoluzione inferiore a 80 megapixel."));
    if (!source.naturalWidth || !source.naturalHeight) throw new Error(tr("Immagine non leggibile."));
    // Keep proportions and transparency; no cropping or white background.
    let side = Math.min(3200, Math.max(source.naturalWidth, source.naturalHeight));
    for (let attempt = 0; attempt < 7; attempt++, side = Math.round(side * 0.75)) {
      const scale = Math.min(1, side / Math.max(source.naturalWidth, source.naturalHeight));
      canvas.width = Math.max(1, Math.round(source.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(source.naturalHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) throw new Error(tr("Questo dispositivo non riesce a elaborare l'immagine."));
      context.drawImage(source, 0, 0, canvas.width, canvas.height);
      for (const quality of [0.9, 0.8, 0.68]) {
        const blob = await new Promise(resolve => canvas.toBlob(resolve, file.type === "image/jpeg" ? "image/jpeg" : "image/webp", quality));
        if (!blob) throw new Error(tr("Compressione non riuscita. Riprova con un'altra immagine."));
        if (blob.size <= maxBytes) {
          const extension = {"image/jpeg": "jpg", "image/png": "png", "image/webp": "webp"}[blob.type];
          return new File([blob], file.name.replace(/\.[^.]+$/, "") + "." + extension, {type: blob.type, lastModified: file.lastModified});
        }
      }
    }
    throw new Error(tr("Non è stato possibile ridurre l'immagine. Scegli una versione più piccola."));
  } finally {
    source.onload = null; source.onerror = null;
    URL.revokeObjectURL(url);
    canvas.width = 0; canvas.height = 0;
  }
}

export async function compressImageFormData(config) {
  if (!(config.data instanceof FormData)) return config;
  const data = new FormData();
  for (const [key, value] of config.data.entries()) {
    data.append(key, value instanceof File ? await compressLargeImage(value) : value);
  }
  config.data = data;
  return config;
}
